import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, BellRing, FileText } from "lucide-react";
import { imgUrl } from "@/data/content";
import { CTABand } from "@/components/CTABand";
import { Reveal } from "@/components/Reveal";
import { scrollToHash } from "@/lib/lenis";

const NAV = [
  { id: "executive-brief", label: "Executive Brief" },
  { id: "strategic-context", label: "Strategic Context" },
  { id: "research-desks", label: "Research Desks" },
  { id: "priority-projects", label: "Priority Projects" },
  { id: "stakeholders", label: "Stakeholders" },
  { id: "evidence-base", label: "Evidence Base" },
  { id: "execution-plan", label: "90-Day Plan" },
  { id: "success-measures", label: "Success Measures" },
  { id: "downloads", label: "Downloads" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

const SectionTitle = ({ kicker, title, copy }) => (
  <div className="max-w-3xl">
    <p className="text-xs font-semibold uppercase tracking-[0.28em] text-ocean">{kicker}</p>
    <h2 className="mt-4 font-serif text-2xl leading-tight tracking-tight text-atlantic sm:text-3xl">{title}</h2>
    {copy && <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">{copy}</p>}
  </div>
);

const DevTag = () => (
  <span className="ml-2 inline-block align-middle text-[9px] font-semibold uppercase tracking-[0.2em] text-slate-400">
    In development
  </span>
);

const Pending = ({ dark = false, children }) => (
  <div className={`border border-dashed p-6 ${dark ? "border-white/20 bg-white/5" : "border-atlantic/25 bg-white"}`}>
    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gold">Research and reporting in development.</p>
    <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-white/60" : "text-slate-600"}`}>{children}</p>
  </div>
);

export const DossierLayout = ({ data: d }) => (
  <main data-testid={`${slug(d.country)}-dossier-page`}>
    <div className="border-b border-atlantic/10 bg-sand-dark">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/countries" data-testid="dossier-back-countries" className="group flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-atlantic/70 transition-colors hover:text-ocean">
          <ArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-1" /> All Countries
        </Link>
        <Link to="/research" data-testid="dossier-back-research" className="text-xs font-semibold uppercase tracking-[0.2em] text-atlantic/70 transition-colors hover:text-ocean">
          Research
        </Link>
      </div>
    </div>

    <section className="grain bg-atlantic py-20 lg:py-28">
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 lg:grid-cols-12 lg:px-8">
        <Reveal className="lg:col-span-7">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">Country Dossier · {d.region}</p>
          <h1 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
            {d.country} Country Dossier
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">{d.subtitle}</p>
        </Reveal>
        <Reveal delay={0.15} className="relative lg:col-span-4 lg:col-start-9">
          <div className="absolute -left-4 -top-4 h-full w-full border border-gold/50" aria-hidden="true" />
          <img src={imgUrl(d.image, 900)} alt={d.imageAlt} loading="lazy" className="relative aspect-[4/3] w-full object-cover" />
        </Reveal>
      </div>
    </section>

    <nav data-testid="dossier-section-nav" className="sticky top-[104px] z-30 border-b border-atlantic/10 bg-sand/90 backdrop-blur-xl" aria-label="Dossier sections">
      <div className="mx-auto flex max-w-7xl flex-wrap gap-x-6 gap-y-2 px-5 py-3 lg:px-8">
        {NAV.map((n) => (
          <a
            key={n.id}
            href={`#${n.id}`}
            onClick={(e) => {
              e.preventDefault();
              scrollToHash(`#${n.id}`, -165);
            }}
            data-testid={`dossier-nav-${n.id}`}
            className="text-[11px] font-semibold uppercase tracking-[0.18em] text-atlantic/60 transition-colors hover:text-ocean"
          >
            {n.label}
          </a>
        ))}
      </div>
    </nav>

    <section id="executive-brief" data-testid="dossier-section-executive-brief" className="bg-sand py-20 lg:py-24">
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <Reveal>
          {d.brief ? (
            <SectionTitle kicker="Executive Brief" title={d.brief.title} copy={d.brief.copy} />
          ) : (
            <>
              <SectionTitle kicker="Executive Brief" title="Executive Brief" />
              <div className="mt-8">
                <Pending>
                  The {d.country} executive brief is being prepared. It will set out why {d.country} anchors this
                  dossier series and what the work will support.
                </Pending>
              </div>
            </>
          )}
        </Reveal>
      </div>
    </section>

    <section id="strategic-context" data-testid="dossier-section-strategic-context" className="border-y border-atlantic/10 bg-sand-dark py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {d.goals ? (
          <>
            <SectionTitle kicker="Strategic Context" title={d.contextTitle} copy={d.contextCopy} />
            <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {d.goals.map((g, i) => (
                <Reveal key={g} delay={i * 0.07} className="border border-atlantic/10 bg-white p-6" data-testid={`dossier-goal-${i + 1}`}>
                  <p className="font-serif text-sm text-gold">{String(i + 1).padStart(2, "0")}</p>
                  <p className="mt-3 font-serif text-lg leading-snug text-atlantic">{g}</p>
                </Reveal>
              ))}
            </div>
            <div className="mt-14 max-w-3xl">
              <h3 className="font-serif text-xl tracking-tight text-atlantic sm:text-2xl">The Current Strategic Environment</h3>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{d.country}&apos;s current strategic environment is shaped by:</p>
              <ul className="mt-6 space-y-4">
                {d.factors.map((f) => (
                  <li key={f} className="flex gap-4 border-b border-atlantic/10 pb-4 text-base leading-relaxed text-slate-700">
                    <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </>
        ) : (
          <Reveal>
            <SectionTitle kicker="Strategic Context" title="Strategic Context" />
            <div className="mt-8 max-w-3xl">
              <Pending>
                {d.country}&apos;s national development goals and strategic environment are being researched and
                verified before publication.
              </Pending>
            </div>
          </Reveal>
        )}
      </div>
    </section>

    <section id="research-desks" data-testid="dossier-section-research-desks" className="bg-sand py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionTitle
          kicker="Research Desks"
          title={d.desksTitle || "Five Desks, One Dossier"}
          copy={d.desksCopy || "Each desk pairs priority questions with featured editorial concepts. Concepts are working titles for journalism, podcast, documentary and research outputs."}
        />
        <div className="mt-14 space-y-8">
          {d.desks.map((desk, di) => (
            <Reveal key={desk.id}>
              <article id={desk.id} data-testid={`dossier-desk-${desk.id}`} className="border border-atlantic/10 bg-white">
                <header className="flex flex-wrap items-center gap-4 border-b border-atlantic/10 bg-sand-dark px-7 py-6 sm:px-9">
                  <desk.icon size={22} className="text-ocean" />
                  <h3 className="font-serif text-xl tracking-tight text-atlantic sm:text-2xl">
                    <span className="mr-3 text-sm text-gold">{String(di + 1).padStart(2, "0")}</span>
                    {desk.title}
                  </h3>
                </header>
                {desk.questions ? (
                  <div className="grid grid-cols-1 gap-10 px-7 py-8 sm:px-9 lg:grid-cols-2">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Priority Questions</p>
                      <ol className="mt-5 space-y-4">
                        {desk.questions.map((q, qi) => (
                          <li key={q} className="flex gap-4 text-sm leading-relaxed text-slate-700 sm:text-base">
                            <span className="shrink-0 font-serif text-sm text-gold">{String(qi + 1).padStart(2, "0")}</span>
                            {q}
                          </li>
                        ))}
                      </ol>
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-500">Featured Concepts</p>
                      <ul className="mt-5 space-y-3">
                        {desk.concepts.map((c) => (
                          <li key={c} data-testid={`dossier-concept-${slug(c)}`} className="border border-atlantic/10 bg-sand px-4 py-3">
                            <span className="font-serif text-base leading-snug text-atlantic">{c}</span>
                            <DevTag />
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div className="px-7 py-8 sm:px-9">
                    <Pending>Priority questions and featured concepts for this desk are being developed.</Pending>
                  </div>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section id="priority-projects" data-testid="dossier-section-priority-projects" className="border-y border-atlantic/10 bg-sand-dark py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionTitle
          kicker="Ten Priority Journalism Projects"
          title={d.projectsTitle || "The First Journalism Slate"}
          copy={d.projectsCopy || "The projects below form this dossier's opening journalism programme."}
        />
        {d.projects ? (
          <div className="mt-12 grid grid-cols-1 gap-x-12 md:grid-cols-2">
            {d.projects.map((p, i) => (
              <Reveal key={p} delay={i * 0.04} className="flex items-baseline gap-5 border-b border-atlantic/10 py-5" data-testid={`dossier-project-${i + 1}`}>
                <span className="font-serif text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-base leading-snug text-atlantic sm:text-lg">
                  {p}
                  <DevTag />
                </p>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 max-w-3xl">
            <Pending>The {d.country} priority journalism slate is being developed.</Pending>
          </div>
        )}
      </div>
    </section>

    <section id="stakeholders" data-testid="dossier-section-stakeholders" className="bg-sand py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionTitle
          kicker="Stakeholder Map"
          title="Institutions Relevant to the Work"
          copy="A working map for engagement planning. Listing an institution does not imply affiliation, endorsement or partnership."
        />
        {d.stakeholders ? (
          <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
            {d.stakeholders.map((s) => (
              <Reveal key={s.group} className="border border-atlantic/10 bg-white p-7 sm:p-8" data-testid={`dossier-stakeholders-${slug(s.group)}`}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-ocean">{s.group}</p>
                <ul className="mt-5 space-y-3">
                  {s.items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 max-w-3xl">
            <Pending>Stakeholder mapping for {d.country} is being compiled.</Pending>
          </div>
        )}
      </div>
    </section>

    <section id="evidence-base" data-testid="dossier-section-evidence-base" className="border-y border-atlantic/10 bg-sand-dark py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionTitle
          kicker="Evidence Base"
          title="Maintained From Primary and Authoritative Sources"
          copy="The dossier is maintained from primary and authoritative sources. No figure is published here until it can be traced to one of them."
        />
        {d.sources ? (
          <div className="mt-10 flex flex-wrap gap-3">
            {d.sources.map((s) => (
              <span key={s} data-testid={`dossier-source-${slug(s)}`} className="border border-atlantic/15 bg-white px-4 py-2.5 text-xs font-semibold tracking-wide text-atlantic/80">
                {s}
              </span>
            ))}
          </div>
        ) : (
          <div className="mt-10 max-w-3xl">
            <Pending>The {d.country} evidence base is being assembled from primary and authoritative sources.</Pending>
          </div>
        )}
      </div>
    </section>

    <section id="execution-plan" data-testid="dossier-section-execution-plan" className="bg-sand py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionTitle kicker="90-Day Execution Plan" title="From Evidence Base to Institution" />
        {d.plan ? (
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {d.plan.map((p, i) => (
              <Reveal key={p.phase} delay={i * 0.08} className="border border-atlantic/10 bg-white p-7" data-testid={`dossier-plan-${i + 1}`}>
                <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-ocean">{p.phase}</p>
                <p className="mt-4 font-serif text-xl leading-snug text-atlantic sm:text-2xl">{p.title}</p>
                <p className="mt-5 border-t border-dashed border-atlantic/15 pt-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                  In development
                </p>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 max-w-3xl">
            <Pending>The 90-day execution plan for the {d.country} dossier is being prepared.</Pending>
          </div>
        )}
      </div>
    </section>

    <section id="success-measures" data-testid="dossier-section-success-measures" className="grain bg-atlantic py-20 lg:py-24">
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">Success Measures — Year One</p>
        <h2 className="mt-4 max-w-3xl font-serif text-2xl leading-tight tracking-tight text-white sm:text-3xl">
          What the First Year Must Produce
        </h2>
        {d.measures ? (
          <div className="mt-12 grid grid-cols-2 gap-px bg-white/10 sm:grid-cols-4">
            {d.measures.map((m) => (
              <div key={m.label} data-testid={`dossier-measure-${slug(m.label)}`} className="bg-atlantic p-6 sm:p-7">
                <p className="font-serif text-3xl text-gold sm:text-4xl">{m.figure}</p>
                <p className="mt-3 text-sm leading-relaxed text-white/70">{m.label}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 max-w-3xl">
            <Pending dark>Success measures will be published with the first {d.country} research cycle.</Pending>
          </div>
        )}
      </div>
    </section>

    <section id="downloads" data-testid="dossier-section-downloads" className="bg-sand py-20 lg:py-24">
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <SectionTitle
          kicker="Downloads"
          title="Dossier Documents"
          copy="Downloadable versions are prepared once each research cycle is complete and verified."
        />
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {d.downloads.map((doc) => (
            <div key={doc.title} className="border border-atlantic/10 bg-white p-6">
              <FileText size={18} className="text-ocean" />
              <p className="mt-3 font-serif text-lg text-atlantic">{doc.title}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">{doc.note}</p>
              <button
                data-testid={doc.testid}
                disabled
                className="mt-5 w-full cursor-not-allowed border border-atlantic/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400"
              >
                {doc.button}
              </button>
            </div>
          ))}
        </div>
        <Link to="/#newsletter" data-testid="dossier-notify-link" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-ocean underline-offset-4 hover:underline">
          <BellRing size={15} /> Get notified when downloads are published
        </Link>

        {d.latestStory && (
          <div id="latest-stories" data-testid="dossier-section-latest" className="mt-16">
            <h2 className="font-serif text-2xl tracking-tight text-atlantic sm:text-3xl">Latest Stories</h2>
            <Link
              to={d.latestStory.to}
              data-testid="dossier-latest-story-card"
              className="group mt-6 flex items-baseline gap-5 border border-atlantic/10 bg-white p-6 transition-colors hover:border-gold"
            >
              <span className="flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-ocean">{d.latestStory.label}</span>
                <span className="mt-2 block font-serif text-lg leading-snug text-atlantic group-hover:text-ocean sm:text-xl">
                  {d.latestStory.title}
                </span>
              </span>
              <ArrowUpRight size={16} className="shrink-0 text-atlantic/40 transition-colors group-hover:text-gold" />
            </Link>
          </div>
        )}

        <div id="collaborate" data-testid="dossier-section-collaborate" className="mt-16">
          <h2 className="font-serif text-2xl tracking-tight text-atlantic sm:text-3xl">Opportunities for Collaboration</h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600">
            Atlantic Bridge welcomes research partnerships, newsroom collaborations and institutional enquiries related
            to {d.country} and wider Africa–CARICOM cooperation.
          </p>
          <Link
            to="/contact"
            data-testid="dossier-collaborate-button"
            className="group mt-7 inline-flex items-center gap-2 bg-atlantic px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-atlantic-light"
          >
            Propose a Collaboration
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
    </section>

    {d.editorialStandard && (
      <section id="editorial-standard" data-testid="dossier-section-editorial-standard" className="grain bg-atlantic-deep py-20 lg:py-24">
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-gold">Editorial Standard</p>
          <p className="mt-7 font-serif text-2xl italic leading-relaxed text-white sm:text-3xl">
            “{d.editorialStandard}”
          </p>
        </div>
      </section>
    )}

    <CTABand {...d.cta} />
  </main>
);
